import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Dropdown from "react-bootstrap/Dropdown";
import { AvatarGroup } from "@fluentui/react-components";
import MemberIconMultiOwner from "../member-icon-new/member-icon-multi-owner";
import MemberDropdownProject from "../member-dopdown-project/member-dropdown-project";
import PlusIconPanelNew from "../plus-icon/Plus-Icon-Panel-New.jsx";
import PlusIcon from "../plus-icon/Plus-Icon-Fluent";
import "./view-members-projects.scss";
import { getOwnerUsers, getOwnerGroups, getUniqueGroups, sortByName } from "./view-members-project-helper";
import { FluentTooltip } from "../FluentUI/Fluent-Tooltip.jsx";

const ViewMembersPanelMultiOwner = ({
  UserID,
  members,
  taskType,
  projects,
  projectsUsers,
  projectsGroups,
  onEdit,
  isGroup,
  textChange = false,
  memberAddDisabled,
  isHidden = false,
  IsMultiOwner = true,
}) => {
  const [allMembers, setAllMembers] = useState([]);
  const [extraMembers, setExtraMembers] = useState(0);
  const [membersShown, setMembersShown] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [owners, setOwners] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const avatarGroupRef = useRef(null);

  useEffect(() => {
    extractMembers();
  }, [projects, projectsGroups, members]);

  const extractMembers = () => {
    const allOwners = projects?.Owners || [];
  
    const ownerUsers = getOwnerUsers(allOwners, projectsUsers);
    const ownerGroups = getOwnerGroups(allOwners, projectsGroups);
    const uniqueOwnerGroups = getUniqueGroups(ownerGroups);
  
    const nonOwnerMembers = members.filter(
      member =>
        !ownerUsers.some(owner => owner.Id === member.UserId) &&
        !uniqueOwnerGroups.some(group => group.GroupId === member.GroupId)
    );
  
    const sortedOwners = sortByName([...ownerUsers, ...uniqueOwnerGroups]);
    const sortedNonOwners = sortByName(nonOwnerMembers);
    const sortedList = [...sortedOwners, ...sortedNonOwners];
  
    setAllMembers(sortedList);
    setOwners(sortedOwners);
  
    const calculatedExtraMembers = sortedList.length > 3 ? sortedList.length - 3 : 0;
    setExtraMembers(calculatedExtraMembers);

    setMembersShown(sortedList.slice(0, 3));
  };

  const handlePlusIconClick = () => {
    setIsOpen(prev => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Opening/closing is hover-driven; the close is deferred briefly so slowly
  // crossing from the avatar row down into the panel cannot dismiss it on the
  // way (QA feedback, TAD-5898).
  const closeTimerRef = useRef(null);

  const cancelScheduledClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPanel = () => {
    cancelScheduledClose();
    setShowDropdown(true);
  };

  const closePanel = () => {
    cancelScheduledClose();
    setShowDropdown(false);
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => setShowDropdown(false), 250);
  };

  useEffect(() => cancelScheduledClose, []);
  
  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center" }}
        data-testid="view-members-hover-area"
        onMouseEnter={cancelScheduledClose}
        onMouseLeave={scheduleClose}
      >
        <Dropdown drop="down" show={showDropdown} onToggle={() => {}} className="removeArrow">
          <Dropdown.Toggle
            as="div"
            className="member-dropdown-toggle"
            onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", padding: 0 }}
          >
            <div
              ref={avatarGroupRef}
              data-testid="member-avatars"
              onMouseEnter={openPanel}
              style={{ display: "flex", alignItems: "center" }}
            >
              <AvatarGroup layout="stacked" size={24}>
                {membersShown.map((member, index) => (
                  <div key={`member-${index}`} style={{ zIndex: membersShown.length + 3 - index }}>
                    <MemberIconMultiOwner member={member || { Name: "N/A", Initials: "NA" }} />
                  </div>
                ))}
                {extraMembers > 0 && (
                  <div style={{ zIndex: 2 }}>
                    <MemberIconMultiOwner extraMembers={extraMembers} type="extra" />
                  </div>
                )}
              </AvatarGroup>
            </div>

            {!isHidden && (
              <FluentTooltip
                text={
                  memberAddDisabled
                    ? "Add Members"
                    : "You don't have access to edit projects. Please contact administration for assistance."
                }
                positioning="above-start"
                target={
                  <div
                    className="plus-icon-wrapper"
                    style={{ marginLeft: "-2px", zIndex: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlusIconClick();
                    }}
                    onMouseEnter={closePanel}
                  >
                    <PlusIcon active={isOpen} disabled={!memberAddDisabled} projectId={projects?.Id} />
                  </div>
                }
              />
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="show-member-panel"
            // Pull the panel up so it overlaps the avatar row — a vertical gap
            // creates a hover dead zone that dismisses the panel mid-crossing
            // (QA feedback, TAD-5898).
            popperConfig={{ modifiers: [{ name: "offset", options: { offset: [0, -6] } }] }}
          >
            <div className="member-dropdown-row-header">
              <div className="member-dropdown-title-text">
                {textChange ? "Actionable by" : "View Members"}
              </div>
            </div>
            <Dropdown.Divider />
            <div className="member-dropdown-body-wrapper">
              {allMembers.map((member, index) => (
                <div key={`dropdown-member-${index}`}>
                  <MemberDropdownProject
                    member={member || { Name: "N/A", Initials: "NA" }}
                    taskType={taskType}
                    filter={false}
                    toggleRow={() => {}}
                    selectedMembers={[]}
                    projects={projects}
                    isGroupOwner={true}
                    isUserAdminComponent={false}
                    IsMultiOwner={IsMultiOwner}
                  />
                  <Dropdown.Divider />
                </div>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {isOpen && (
        <div className="plus-icon-panel-view-members">
          <PlusIconPanelNew
            userID={UserID}
            projects={projects}
            onEdit={onEdit}
            usersMembers={projectsUsers}
            groupsMembers={projectsGroups}
            selectedMembers={[...allMembers]}
            close={handleClose}
            owners={owners}
            IsMultiOwner={IsMultiOwner}
          />
        </div>
      )}
    </div>
  );
};

ViewMembersPanelMultiOwner.propTypes = {
  UserID: PropTypes.number.isRequired,
  members: PropTypes.array.isRequired,
  projectsUsers: PropTypes.array.isRequired,
  projectsGroups: PropTypes.array.isRequired,
  projects: PropTypes.shape({
    Id: PropTypes.number,
    Owners: PropTypes.arrayOf(PropTypes.shape({
      Id: PropTypes.number,
      IsGroup: PropTypes.bool,
      Group: PropTypes.object, 
    })),
    GroupId: PropTypes.number,
    Group: PropTypes.shape({
      Name: PropTypes.string,
    }),
    Initials: PropTypes.string,
  }).isRequired,
  isGroup: PropTypes.bool.isRequired,
  taskType: PropTypes.string.isRequired,
  isHidden: PropTypes.bool.isRequired,
  memberAddDisabled: PropTypes.bool.isRequired,
  onEdit: PropTypes.func,
  textChange: PropTypes.bool,
  IsMultiOwner: PropTypes.bool,
};

export default ViewMembersPanelMultiOwner;

ViewMembersPanelMultiOwner.defaultProps = {
  IsMultiOwner: true,
  onEdit: () => {},
};
